'use client';

import { motion } from 'framer-motion';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import { useCallback } from 'react';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { siteConfig } from '@/lib/config/site';

import { useLandingFAQTracking } from '../../hooks/useLandingFAQTracking';
import { useSectionVisibility } from '../../hooks/useSectionVisibility';

type FaqItem = {
  value: string;
  key: string;
};

const faqItems: FaqItem[] = [
  { value: 'item-1', key: 'item1' },
  { value: 'item-2', key: 'item2' },
  { value: 'item-3', key: 'item3' },
  { value: 'item-4', key: 'item4' },
  { value: 'item-5', key: 'item5' },
  { value: 'item-6', key: 'item6' },
  { value: 'item-7', key: 'item7' },
  { value: 'item-8', key: 'item8' },
  { value: 'item-9', key: 'item9' },
  { value: 'item-10', key: 'item10' },
  { value: 'item-11', key: 'item11' },
  { value: 'item-12', key: 'item12' },
];

const FAQSection = () => {
  const t = useTranslations('landing.faq');
  const { ref } = useSectionVisibility('faq');
  const { trackFAQExpanded, trackFAQCTAClicked } = useLandingFAQTracking();

  const handleAccordionChange = useCallback(
    (value: string) => {
      if (value) {
        const index = faqItems.findIndex((item) => item.value === value);
        const item = faqItems[index];
        if (item) {
          trackFAQExpanded({
            questionId: item.value,
            questionKey: item.key,
            questionIndex: index,
          });
        }
      }
    },
    [trackFAQExpanded],
  );

  const handleCTAClick = useCallback(() => {
    trackFAQCTAClicked({
      ctaText: t('footer.cta'),
      destinationUrl: siteConfig.bookingUrl,
    });
  }, [trackFAQCTAClicked, t]);

  return (
    <section ref={ref} id="faq" className="py-24 sm:py-32 bg-muted border-t border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-2xl text-center mb-16">
          <Badge
            variant="outline"
            className="mb-6 px-4 py-1.5 text-sm border-border bg-muted/30 text-muted-foreground"
          >
            {t('badge')}
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl mb-4 text-foreground">
            {t.rich('title', {
              highlight: (chunks) => <span className="text-primary">{chunks}</span>,
            })}
          </h2>
          <p className="text-lg text-muted-foreground">{t('subtitle')}</p>
        </div>

        {/* FAQ Content */}
        <div className="max-w-3xl mx-auto">
          <div className="space-y-4">
            <Accordion
              type="single"
              collapsible
              className="w-full space-y-4"
              onValueChange={handleAccordionChange}
            >
              {faqItems.map((item, index) => (
                <motion.div
                  key={item.value}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                  viewport={{ once: true }}
                >
                  <AccordionItem
                    value={item.value}
                    className="rounded-xl border border-border bg-card px-6 overflow-hidden data-[state=open]:border-input data-[state=open]:bg-card/80"
                  >
                    <AccordionTrigger className="hover:no-underline py-5 text-foreground">
                      <span className="text-base font-medium text-foreground group-hover:text-primary transition-colors text-left">
                        {t(`items.${item.key}.question`)}
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground pb-5 leading-relaxed">
                      {t(`items.${item.key}.answer`)}
                    </AccordionContent>
                  </AccordionItem>
                </motion.div>
              ))}
            </Accordion>
          </div>

          {/* Contact Support CTA */}
          <div className="text-center mt-12">
            <p className="text-muted-foreground mb-4">{t('footer.text')}</p>
            <Button
              className="cursor-pointer bg-primary text-primary-foreground hover:bg-primary/90 shadow-lg shadow-primary/20 hover:scale-105 transition-all"
              size="lg"
              asChild
              onClick={handleCTAClick}
            >
              <Link href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer">
                {t('footer.cta')}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export { FAQSection };
